import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SyncIcon from "@mui/icons-material/Sync";
import { FlagCell, PaymentDateCell, PaymentRuleCell, RealizedCell } from "../../components/companyReview/cells";
import { isNotValidated, situationOf, SITUATION_LABEL } from "../../components/companyReview/vacancyState";
import { formatBRL, formatDateBR } from "../../shared/ui/format";
import { formatDateInHoursAndMinutes } from "../../utils/date";

export type VacancyRow = {
  id: number;
  hospital_name: string;
  date: string;
  start_hour?: string | null;
  end_hour?: string | null;
  real_start_hour?: string | null;
  real_end_hour?: string | null;
  gross_value: number | null;
  real_net_value: number | null;
  normal_payment_date?: string | null;
  verified_documents?: boolean;
  can_advance?: boolean;
  is_checked?: boolean;
  payment_rule?: string;
  validated?: boolean;
};

// Mesmo objeto `handlersRef.current` que a tabela usa (CompanyPackageReview.js): cartões e tabela gravam igual.
export type VacancyHandlers = {
  setDraft: (rowId: number, field: string, value: string) => void;
  commitDraft: (rowId: number, field: string) => void;
  toggleFlag: (rowId: number, field: string, checked: boolean) => void;
  changeRule: (rowId: number, value: string) => void;
  syncRow: (rowId: number) => void;
};

export type VacancyCardsProps = {
  packageId: number;
  rows: VacancyRow[];
  selection: number[];
  onSelectionChange: (ids: number[]) => void;
  drafts: Record<string, string>;
  pendingIds: number[];
  handlers: VacancyHandlers;
};

const SITUATION_COLORS: Record<string, { bg: string; fg: string }> = {
  no: { bg: "#f1f2f4", fg: "#6b7280" },
  ok: { bg: "#e6f4ea", fg: "#1e6b34" },
  review: { bg: "#fff4e0", fg: "#8a5200" },
  pending: { bg: "#eef2f6", fg: "#475569" },
};
const hourOf = (value?: string | null) => (value ? formatDateInHoursAndMinutes(new Date(value)) : "");
const FLAGS: [string, string][] = [
  ["verified_documents", "Documentos verificados"],
  ["can_advance", "Pode adiantar"],
  ["is_checked", "Revisado"],
];

export default function VacancyCards({
  packageId,
  rows,
  selection,
  onSelectionChange,
  drafts,
  pendingIds,
  handlers,
}: VacancyCardsProps) {
  const toggle = (id: number) =>
    onSelectionChange(selection.includes(id) ? selection.filter((x) => x !== id) : [...selection, id]);
  return (
    <Box
      component="ul"
      aria-label={`Plantões do pacote ${packageId}`}
      sx={{
        listStyle: "none",
        p: 0,
        m: 0,
        display: "grid",
        gap: 1.25,
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      }}
    >
      {rows.map((row) => {
        const situation = situationOf(row) as string;
        const busy = pendingIds.includes(row.id);
        const selected = selection.includes(row.id);
        const colors = SITUATION_COLORS[situation] ?? SITUATION_COLORS.pending;
        return (
          <Paper
            key={row.id}
            component="li"
            variant="outlined"
            data-situation={situation}
            sx={{
              p: 1.25,
              borderColor: selected ? "primary.main" : situation === "review" ? "#f0c36d" : "divider",
              bgcolor: situation === "review" ? "#fffbf0" : "#fff",
              opacity: situation === "no" ? 0.65 : 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              <Checkbox
                size="small"
                checked={selected}
                onChange={() => toggle(row.id)}
                sx={{ p: 0.5 }}
                inputProps={{ "aria-label": `Selecionar plantão ${row.id}` }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>#{row.id}</Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>· {formatDateBR(row.date)}</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                size="small"
                label={SITUATION_LABEL[situation as keyof typeof SITUATION_LABEL]}
                sx={{ height: 22, fontSize: 11.5, bgcolor: colors.bg, color: colors.fg }}
              />
            </Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 500, mt: 0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {row.hospital_name}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: 1,
                rowGap: 0.5,
                mt: 0.75,
                fontSize: 12,
                alignItems: "center",
              }}
            >
              <span style={{ color: "#6b7280" }}>Previsto</span>
              <span>{`${hourOf(row.start_hour)} – ${hourOf(row.end_hour)}`}</span>
              <span style={{ color: "#6b7280" }}>Realizado</span>
              <RealizedCell
                row={row}
                drafts={drafts}
                disabled={busy}
                onDraft={handlers.setDraft}
                onCommit={handlers.commitDraft}
              />
              <span style={{ color: "#6b7280" }}>Valor bruto</span>
              <span>
                {isNotValidated(row) ? (
                  <b style={{ color: "#b26a00" }}>⚠ Valor não validado</b>
                ) : (
                  formatBRL(row.gross_value)
                )}
              </span>
              <span style={{ color: "#6b7280" }}>Valor proporcional</span>
              <span>{formatBRL(row.real_net_value)}</span>
              <span style={{ color: "#6b7280" }}>Pagamento normal</span>
              <PaymentDateCell
                rowId={row.id}
                value={row.normal_payment_date}
                draft={drafts[`${row.id}.normal_payment_date`]}
                disabled={busy}
                onDraft={handlers.setDraft}
                onCommit={handlers.commitDraft}
              />
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", mt: 0.5, fontSize: 12 }}>
              {FLAGS.map(([field, label]) => (
                <Box key={field} component="label" sx={{ display: "inline-flex", alignItems: "center", mr: 1 }}>
                  <FlagCell
                    rowId={row.id}
                    field={field}
                    label={label}
                    value={row[field as keyof VacancyRow]}
                    disabled={busy}
                    onToggle={handlers.toggleFlag}
                  />
                  {label}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <PaymentRuleCell rowId={row.id} value={row.payment_rule} disabled={busy} onChange={handlers.changeRule} />
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Sincronizar com o Minha Escala">
                <IconButton size="small" aria-label={`Sincronizar plantão ${row.id}`} onClick={() => handlers.syncRow(row.id)}>
                  <SyncIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
